import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';

export default class TKFileLink extends TKCustomElementFactory {
    file: string;
    linkText: string;
    description: string;
    isDownloadingClassName: string;
    isDoneClassName: string;

    constructor() {
        super();

        this.file = this.getDataAttribute('file', '');
        this.linkText = this.getDataAttribute('linktext', 'Download');
        this.description = this.getDataAttribute('description', this.linkText);
        this.isDownloadingClassName = this.getDataAttribute(
            'is-downloading-class-name',
            'tk-file-card--is-downloading',
        );
        this.isDoneClassName = this.getDataAttribute('is-done-class-name', 'tk-file-card--is-done');
    }

    connectedCallback(): void {
        this.render();
        this.registerClickListener();
    }

    render(): void {
        if (!this.file) {
            this.innerHTML = `<span class="tk-file-link__missing">No file provided</span>`;
            return;
        }

        const fileName = this.linkText || this.file.split('/').pop();

        this.innerHTML = `
            <div
                class="tk-file-card"
                role="button" tabindex="0"
                data-tk-file-card
            >
                <div class="tk-file-card__icon">
                    <i class="tk-icon-file"></i>
                </div>

                <div class="tk-file-card__info">
                    <div class="tk-file-card__filename">${fileName}</div>
                    <div class="tk-file-card__meta">${this.description}</div>
                </div>

                <div class="tk-file-card__progress-wrapper">
                    <div class="tk-file-card__download-icon">
                        <i class="tk-icon-file-download"></i>
                    </div>
                    <svg class="tk-file-card__progress" viewBox="0 0 36 36">
                        <path class="tk-file-card__progress-bg"
                            d="M18 2.0845
                            a 15.9155 15.9155 0 0 1 0 31.831
                            a 15.9155 15.9155 0 0 1 0 -31.831"/>
                        <path
                            class="tk-file-card__progress-fill"
                            data-tk-progress-path
                            stroke-dasharray="0, 100"
                            d="M18 2.0845
                            a 15.9155 15.9155 0 0 1 0 31.831
                            a 15.9155 15.9155 0 0 1 0 -31.831"/>
                    </svg>
                    <div class="tk-file-card__check-icon">
                        <i class="tk-icon-check"></i>
                    </div>
                </div>
            </div>
        `;
    }

    registerClickListener() {
        const card = this.querySelector('[data-tk-file-card]');
        if (!card) return;

        this.pushListener({
            event: 'click',
            element: card,
            action: () => this.startJsDownload(),
        });
    }

    async startJsDownload() {
        this.classList.remove(this.isDoneClassName);
        this.classList.add(this.isDownloadingClassName);
        this.setProgress(0);

        const response = await fetch(this.file);
        if (!response.ok) {
            console.error('Download failed:', response.status);
            return;
        }

        const contentLength = Number(response.headers.get('Content-Length')) || 0;
        const reader = response.body!.getReader();

        let received = 0;
        const chunks: Uint8Array[] = [];

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            chunks.push(value);
            received += value.length;

            if (contentLength > 0) {
                this.setProgress((received / contentLength) * 100);
            }
        }

        const blob = new Blob(chunks.map((c) => new Uint8Array(c).buffer));
        this.finishDownload(blob);
    }

    finishDownload(blob: Blob) {
        this.classList.remove(this.isDownloadingClassName);
        this.classList.add(this.isDoneClassName);
        this.setProgress(100);

        const url = URL.createObjectURL(blob);
        const filename = this.file.split('/').pop() || 'download';

        const tempLink = document.createElement('a');
        tempLink.href = url;
        tempLink.download = filename;
        tempLink.click();

        URL.revokeObjectURL(url);

        this.hookAfterClickOnLink(new Event('download-finished'));
    }

    setProgress(value: number) {
        const path = this.querySelector('[data-tk-progress-path]') as SVGPathElement;
        if (!path) return;
        const v = Math.max(0, Math.min(100, value));
        path.style.strokeDasharray = `${v}, 100`;
    }

    hookAfterClickOnLink(_event: Event) {}
}
